import { useEffect, useState } from 'react';
import {
	getLobOptions,
	getNaicOptions,
	ImportTypeInsuranceCarrier,
	ImportTypeInsuranceCarrierSchema,
	update,
} from '@/api/admin/insurance-carrier';
import { Button } from '@/components/ui/button';
import { ComboboxSelect } from '@/components/ui/custom/combobox-select';
import { Form, FormField } from '@/components/ui/form';
import {
	Sheet,
	SheetContent,
	SheetDescription,
	SheetHeader,
	SheetTitle,
} from '@/components/ui/sheet';
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from '@/components/ui/tooltip';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Edit } from 'lucide-react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { toast } from 'sonner';

export function UpdateImportTypeInsuranceCarrier({
	importTypeId,
	importTypeInsuranceCarrier,
}: {
	importTypeId: number;
	importTypeInsuranceCarrier: ImportTypeInsuranceCarrier;
}) {
	const [open, setOpen] = useState(false);

	return (
		<div>
			<TooltipProvider>
				<Tooltip>
					<TooltipTrigger asChild>
						<Button className="m-auto" variant="ghost" onClick={() => setOpen(true)}>
							<Edit size={16} />
						</Button>
					</TooltipTrigger>
					<TooltipContent>
						<p>Update Insurance Carrier</p>
					</TooltipContent>
				</Tooltip>
			</TooltipProvider>

			{open && (
				<UpdateSheet
					open={open}
					setOpen={setOpen}
					importTypeId={importTypeId}
					importTypeInsuranceCarrier={importTypeInsuranceCarrier}
				/>
			)}
		</div>
	);
}

function UpdateSheet({
	open,
	setOpen,
	importTypeId,
	importTypeInsuranceCarrier,
}: {
	open: boolean;
	setOpen: (open: boolean) => void;
	importTypeId: number;
	importTypeInsuranceCarrier: ImportTypeInsuranceCarrier;
}) {
	const queryClient = useQueryClient();

	const form = useForm<ImportTypeInsuranceCarrier>({
		resolver: zodResolver(ImportTypeInsuranceCarrierSchema),
		defaultValues: importTypeInsuranceCarrier,
		mode: 'all',
	});

	const { reset, watch } = form;

	const { data: lobOptions, isLoading: isLoadingLobOptions } = useQuery({
		queryKey: [`lob-options`, importTypeId],
		queryFn: () => getLobOptions(importTypeId),
		staleTime: 5 * 60 * 1000,
	});

	const [initialLobId] = useState(importTypeInsuranceCarrier.importTypeLobId);
	const selectedLobId = watch('importTypeLobId');
	const { data: naicOptions, isLoading: isLoadingNaicOptions } = useQuery({
		queryKey: [`naic-options`, selectedLobId, importTypeInsuranceCarrier.naicId],
		queryFn: () =>
			getNaicOptions(selectedLobId!, importTypeInsuranceCarrier.naicId),
		staleTime: 5 * 60 * 1000,
		enabled: !!selectedLobId,
	});

	useEffect(() => {
		if (selectedLobId !== initialLobId) {
			form.resetField('naicId');
		}
	}, [selectedLobId, initialLobId, form]);

	useEffect(() => {
		reset(importTypeInsuranceCarrier);
	}, [importTypeInsuranceCarrier, reset]);

	const { mutate } = useMutation({
		mutationFn: (updatedInsuranceCarrier: ImportTypeInsuranceCarrier) =>
			update(updatedInsuranceCarrier),
		onSuccess: () => {
			toast.success(`Success! Insurance Carrier has been successfully updated.`, {
				dismissible: true,
			});
			setOpen(false);

			//Invalidate queries to refresh data
			queryClient.invalidateQueries({
				queryKey: [`insuranceCarriers-${importTypeId}`],
			});
			queryClient.invalidateQueries({
				queryKey: [`naic-options`],
			});
		},
		onError: () => {
			toast.error(`Error! Insurance Carrier could not be updated.`, {
				dismissible: true,
			});
		},
	});

	const onSubmit: SubmitHandler<ImportTypeInsuranceCarrier> = (values) =>
		mutate(values);

	return (
		<Sheet open={open} onOpenChange={setOpen}>
			<SheetContent>
				<SheetHeader>
					<SheetTitle>Update Insurance Carrier</SheetTitle>
					<SheetDescription></SheetDescription>
				</SheetHeader>
				<Form {...form}>
					<form onSubmit={form.handleSubmit(onSubmit)}>
						<div className="space-y-2">
							<FormField
								control={form.control}
								name="importTypeLobId"
								render={({ field }) => (
									<ComboboxSelect
										field={field}
										options={lobOptions}
										valueKey="value"
										displayKey="label"
										isLoading={isLoadingLobOptions}
										label="Import Type LOB"
										placeholder="Select import type LOB..."
										searchPlaceholder="Search import type LOB..."
										required={true}
										emptyMessage="No import type LOB found."
										loadingMessage="Loading import type LOB..."
									/>
								)}
							/>
							<FormField
								control={form.control}
								name="naicId"
								render={({ field }) => (
									<ComboboxSelect
										field={field}
										options={naicOptions}
										valueKey="value"
										displayKey="label"
										isLoading={isLoadingNaicOptions}
										label="Company Name"
										placeholder={
											selectedLobId
												? 'Select company name...'
												: 'Select an Import Type LOB first'
										}
										searchPlaceholder="Search company name..."
										required={true}
										emptyMessage="No company name found."
										loadingMessage="Loading company name..."
										disabled={!selectedLobId}
									/>
								)}
							/>
						</div>

						<div className="mt-9 flex justify-end">
							<Button
								type="submit"
								disabled={!form.formState.isValid || form.formState.isSubmitting}
							>
								{form.formState.isSubmitting ? 'Updating...' : 'Update'}
							</Button>
						</div>
					</form>
				</Form>
			</SheetContent>
		</Sheet>
	);
}
